import { type ReactNode, useId } from "react";

import * as theme from "./OptionCardGroup.css";

export interface OptionCard<Value extends string> {
    value: Value;
    label: ReactNode;
    description?: ReactNode;
}

export interface OptionCardGroupProps<Value extends string> {
    options: readonly OptionCard<Value>[];
    value: NoInfer<Value>;
    onChange: (value: Value) => void;
    disabled?: boolean;
    "aria-label": string;
}

/** The design's "Radio / Option card". */
export function OptionCardGroup<Value extends string>({
    options,
    value,
    onChange,
    disabled = false,
    "aria-label": ariaLabel,
}: OptionCardGroupProps<Value>) {
    const name = useId();
    return (
        <div role="radiogroup" aria-label={ariaLabel} className={theme.root}>
            {options.map((option) => (
                <label
                    key={option.value}
                    className={theme.card}
                    data-checked={option.value === value || undefined}
                    data-disabled={disabled || undefined}
                >
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={option.value === value}
                        disabled={disabled}
                        onChange={() => onChange(option.value)}
                        className={theme.radio}
                    />
                    <span className={theme.text}>
                        <span className={theme.label}>{option.label}</span>
                        {option.description && (
                            <span className={theme.description}>
                                {option.description}
                            </span>
                        )}
                    </span>
                </label>
            ))}
        </div>
    );
}
